import React from "react";
import { fetchHospitalOptions } from "../../api/hospitals";
import {
  deleteTimecard,
  fetchMyTimecards,
  fetchTimecard,
  resubmitTimecard,
  saveTimecard,
  submitTimecard
} from "../../api/timecards";
import ApprovalSection from "../ApprovalSection";
import ResubmitAfterEditDialog from "../ResubmitAfterEditDialog";
import AppHeader from "../common/AppHeader";
import SectionHeading from "../common/SectionHeading";
import TimecardEntryArea from "../timecard/TimecardEntryArea";
import TodaySection from "../timecard/TodaySection";
import Toast from "../common/Toast";
import { useToast } from "../../hooks/useToast";
import { DAYS } from "../../constants/timecard";
import {
  addWeeks,
  applyCallBackRowChange,
  applyRegularRowChange,
  buildTimecardPayload,
  calculateCallBackTotalHours,
  calculateCallTotalHours,
  createCallBackRow,
  createCallRow,
  createEmptyRows,
  createRegularRow,
  formatLocalDate,
  formatWeekRange,
  getDefaultWeekRange,
  hasNewCompletionTime,
  getNetCallHours,
  isDepartmentValidForHospital,
  mapTimecardToFormState,
  updateRow
} from "../../utils/timecard";
import {
  buildDraftTimecardChoices,
  getWeekOffsetForStartDate,
  isReadyToSubmitSelectedDraft
} from "../../utils/contractorDashboard";

const TIMECARD_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved"
};

function snapshotHasActiveShift(snapshot, currentWeekOffset) {
  if (currentWeekOffset !== 0) {
    return false;
  }

  const today = DAYS[new Date().getDay()];
  const activeRegularShift = snapshot.regularRows.some(
    (row) => row.day === today && row.timeIn && !row.timeOut
  );
  const activeCallShift = snapshot.callRows.some(
    (row) => row.day === today && row.startTime && !row.endTime
  );
  const activeCallbackShift = snapshot.callbackRows.some(
    (row) =>
      row.day === today &&
      ((row.timeInOne && !row.timeOutOne) || (row.timeInTwo && !row.timeOutTwo))
  );

  return activeRegularShift || activeCallShift || activeCallbackShift;
}

export default function ContractorDashboard({ session, onLogout, logoUrl }) {
  const contractorId = Number(session.contractorId);
  const [activeTab, setActiveTab] = React.useState("regular");
  const [hospitalOptions, setHospitalOptions] = React.useState([]);
  const [hospitalLoading, setHospitalLoading] = React.useState(true);
  const [hospitalLoadError, setHospitalLoadError] = React.useState("");
  const [currentWeekOffset, setCurrentWeekOffset] = React.useState(0); // 0 = current week, -1 = last week, 1 = next week
  const [selectedTimecardId, setSelectedTimecardId] = React.useState(0);
  const [weekStartDate, setWeekStartDate] = React.useState(getDefaultWeekRange()[0]);
  const [weekEndDate, setWeekEndDate] = React.useState(getDefaultWeekRange()[1]);
  const [regularRows, setRegularRows] = React.useState(() => createEmptyRows().regularRows);
  const [callRows, setCallRows] = React.useState(() => createEmptyRows().callRows);
  const [callbackRows, setCallbackRows] = React.useState(() => createEmptyRows().callbackRows);
  const [attestationAccepted, setAttestationAccepted] = React.useState(false);
  const [signedByName, setSignedByName] = React.useState("");
  const [signatureValue, setSignatureValue] = React.useState("");
  const [signedDate, setSignedDate] = React.useState("");
  const [currentStatus, setCurrentStatus] = React.useState("draft");
  const [saveError, setSaveError] = React.useState("");
  const { toastMessage, toastVisible, showToast, dismissToast } = useToast();
  const [timecardsLoading, setTimecardsLoading] = React.useState(false);
  const [timecardLoadError, setTimecardLoadError] = React.useState("");
  const [timecards, setTimecards] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showSubmitWeekDialog, setShowSubmitWeekDialog] = React.useState(false);
  const [selectedSubmitDraftId, setSelectedSubmitDraftId] = React.useState(0);
  const [draftSubmitReviewId, setDraftSubmitReviewId] = React.useState(0);
  const [showResubmitAfterEditDialog, setShowResubmitAfterEditDialog] = React.useState(false);
  const lastSavedPayloadRef = React.useRef(null);
  const pendingSilentSaveRef = React.useRef(null);
  const formContextVersionRef = React.useRef(0);
  const submitWeekDialogRef = React.useRef(null);

  const invalidateFormContext = React.useCallback(() => {
    formContextVersionRef.current += 1;
    pendingSilentSaveRef.current = null;
    setShowResubmitAfterEditDialog(false);
  }, []);

  const currentWeekStart = React.useMemo(() => {
    const baseWeek = getDefaultWeekRange()[0];
    const [y, m, d] = baseWeek.split("-").map(Number);
    const baseDate = new Date(y, m - 1, d);
    return addWeeks(baseDate, currentWeekOffset);
  }, [currentWeekOffset]);

  const currentWeekEnd = React.useMemo(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + 6);
    return endDate;
  }, [currentWeekStart]);

  const currentWeekRange = React.useMemo(() => {
    return formatWeekRange(currentWeekStart);
  }, [currentWeekStart]);

  const baseWeekStartDate = React.useMemo(() => getDefaultWeekRange()[0], []);

  const draftTimecards = React.useMemo(
    () => buildDraftTimecardChoices(timecards, baseWeekStartDate),
    [baseWeekStartDate, timecards]
  );

  const readyToSubmitSelectedDraft = React.useMemo(
    () => isReadyToSubmitSelectedDraft(selectedTimecardId, draftSubmitReviewId, currentStatus),
    [currentStatus, draftSubmitReviewId, selectedTimecardId]
  );

  const submitButtonLabel = readyToSubmitSelectedDraft
    ? `Submit timecard for ${currentWeekRange}`
    : "Submit timecard";

  const queueSilentSave = React.useCallback((snapshotOverrides) => {
    pendingSilentSaveRef.current = {
      ...(pendingSilentSaveRef.current ?? {}),
      ...snapshotOverrides
    };
  }, []);

  const queueWorkEntrySave = React.useCallback((snapshotOverrides) => {
    queueSilentSave(snapshotOverrides);
  }, [queueSilentSave]);

  const resetTimecardForm = React.useCallback(() => {
    const emptyRows = createEmptyRows();

    invalidateFormContext();
    setRegularRows(emptyRows.regularRows);
    setCallRows(emptyRows.callRows);
    setCallbackRows(emptyRows.callbackRows);
    setSelectedTimecardId(0);
    setWeekStartDate(formatLocalDate(currentWeekStart));
    setWeekEndDate(formatLocalDate(currentWeekEnd));
    setAttestationAccepted(false);
    setSignedByName(session.displayName ?? "");
    setSignatureValue("");
    setSignedDate("");
    setCurrentStatus("draft");
    setSaveError("");

    lastSavedPayloadRef.current = JSON.stringify({
      timecardId: 0,
      contractorId,
      weekStartDate: formatLocalDate(currentWeekStart),
      weekEndDate: formatLocalDate(currentWeekEnd),
      status: "draft",
      attestationAccepted: false,
      signedByName: session.displayName ?? "",
      signatureValue: "",
      signedDate: "",
      regularRows: emptyRows.regularRows,
      callRows: emptyRows.callRows,
      callbackRows: emptyRows.callbackRows,
      hospitalOptions
    });
  }, [contractorId, currentWeekEnd, currentWeekStart, hospitalOptions, invalidateFormContext, session.displayName]);

  const handleRegularRowChange = React.useCallback(
    (index, field, value) => {
      const nextRegularRows = applyRegularRowChange(regularRows, index, field, value, hospitalOptions);
      setRegularRows(nextRegularRows);
      queueWorkEntrySave({ regularRows: nextRegularRows });
    },
    [hospitalOptions, queueWorkEntrySave, regularRows]
  );

  const handleRegularTookBreakChange = React.useCallback(
    (index, updates) => {
      const nextRegularRows = regularRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...updates } : row
      );

      setRegularRows(nextRegularRows);
      queueWorkEntrySave({ regularRows: nextRegularRows });
    },
    [queueWorkEntrySave, regularRows]
  );

  const handleCallRowChange = React.useCallback(
    (index, field, value) => {
      if (field === "hospital") {
        const nextCallRows = callRows.map((row, rowIndex) => {
          if (rowIndex !== index) {
            return row;
          }

          const updatedRow = {
            ...row,
            hospital: value,
            department: isDepartmentValidForHospital(hospitalOptions, value, row.department)
              ? row.department
              : ""
          };
          const cbRow = callbackRows.find(cb => cb.day === updatedRow.day);
          const cbHours = cbRow ? calculateCallBackTotalHours(cbRow, value, hospitalOptions) : "";
          return {
            ...updatedRow,
            totalHours: getNetCallHours(calculateCallTotalHours(updatedRow, hospitalOptions), cbHours)
          };
        });
        const selectedDay = callRows[index]?.day;
        const nextCallbackRows = callbackRows.map((row) =>
          row.day === selectedDay
            ? {
                ...row,
                totalHours: calculateCallBackTotalHours(row, value, hospitalOptions)
              }
            : row
        );

        setCallRows(nextCallRows);
        setCallbackRows(nextCallbackRows);
        queueWorkEntrySave({
          callRows: nextCallRows,
          callbackRows: nextCallbackRows
        });
        return;
      }

      if (field === "startTime" || field === "endTime") {
        const nextCallRows = callRows.map((row, rowIndex) => {
          if (rowIndex !== index) return row;
          const updatedRow = { ...row, [field]: value };
          const cbRow = callbackRows.find(cb => cb.day === updatedRow.day);
          return { ...updatedRow, totalHours: getNetCallHours(calculateCallTotalHours(updatedRow, hospitalOptions), cbRow?.totalHours ?? "") };
        });
        setCallRows(nextCallRows);
        queueWorkEntrySave({ callRows: nextCallRows });
        return;
      }

      const nextCallRows = callRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      );
      setCallRows(nextCallRows);
      queueWorkEntrySave({ callRows: nextCallRows });
    },
    [callRows, callbackRows, hospitalOptions, queueWorkEntrySave]
  );

  const handleCallBackRowChange = React.useCallback(
    (index, field, value) => {
      const { callbackRows: nextCallbackRows, callRows: nextCallRows } =
        applyCallBackRowChange(callbackRows, callRows, index, field, value, hospitalOptions);
      setCallbackRows(nextCallbackRows);
      if (nextCallRows !== callRows) {
        setCallRows(nextCallRows);
        queueWorkEntrySave({ callbackRows: nextCallbackRows, callRows: nextCallRows });
      } else {
        queueWorkEntrySave({ callbackRows: nextCallbackRows });
      }
    },
    [callbackRows, callRows, hospitalOptions, queueWorkEntrySave]
  );

  const handleAttestationChange = React.useCallback((value) => {
    setAttestationAccepted(value);
    if (value) {
      const today = formatLocalDate(new Date());
      const name = session.displayName ?? "";
      setSignedByName(name);
      setSignatureValue(name);
      setSignedDate(today);
      queueSilentSave({ attestationAccepted: value, signedByName: name, signatureValue: name, signedDate: today });
    } else {
      setSignedByName("");
      setSignatureValue("");
      setSignedDate("");
      queueSilentSave({ attestationAccepted: value, signedByName: "", signatureValue: "", signedDate: "" });
    }
  }, [queueSilentSave, session.displayName]);

  const handleSignedByNameChange = React.useCallback((value) => {
    setSignedByName(value);
    queueSilentSave({ signedByName: value });
  }, [queueSilentSave]);

  const handleSignedDateChange = React.useCallback((value) => {
    setSignedDate(value);
    queueSilentSave({ signedDate: value });
  }, [queueSilentSave]);

  const handleAddRegularRow = React.useCallback((hospital) => {
    const today = DAYS[new Date().getDay()];
    const newRow = { ...createRegularRow(today), date: formatLocalDate(new Date()), hospital: hospital || "" };
    const nextRows = [...regularRows, newRow];
    setRegularRows(nextRows);
    queueWorkEntrySave({ regularRows: nextRows });
  }, [regularRows, queueWorkEntrySave]);

  const handleClearTodayRow = React.useCallback((shiftType, rowIndex) => {
    const today = DAYS[new Date().getDay()];

    if (shiftType === "regular") {
      if (rowIndex !== undefined && rowIndex >= DAYS.length) {
        const nextRows = regularRows.filter((_, i) => i !== rowIndex);
        setRegularRows(nextRows);
        queueWorkEntrySave({ regularRows: nextRows });
      } else {
        const nextRows = regularRows.map((row, i) => {
          if (rowIndex !== undefined ? i === rowIndex : row.day === today) {
            return { ...createRegularRow(today), date: row.date };
          }
          return row;
        });
        setRegularRows(nextRows);
        queueWorkEntrySave({ regularRows: nextRows });
      }
    } else if (shiftType === "call") {
      const nextRows = callRows.map(row => {
        if (row.day !== today) return row;
        return { ...createCallRow(today), date: row.date };
      });
      setCallRows(nextRows);
      queueWorkEntrySave({ callRows: nextRows });
    } else if (shiftType === "callback") {
      const nextRows = callbackRows.map(row => {
        if (row.day !== today) return row;
        return createCallBackRow(today);
      });
      setCallbackRows(nextRows);
      queueWorkEntrySave({ callbackRows: nextRows });
    }
  }, [callRows, callbackRows, queueWorkEntrySave, regularRows]);

  const getCurrentTimecardSnapshot = React.useCallback(
    (payload) => {
      const snapshot = payload ?? {
        timecardId: selectedTimecardId,
        contractorId,
        weekStartDate,
        weekEndDate,
        status: currentStatus,
        attestationAccepted,
        signedByName,
        signatureValue,
        signedDate,
        regularRows,
        callRows,
        callbackRows,
        hospitalOptions
      };

      return {
        timecardId: snapshot.timecardId,
        contractorId: snapshot.contractorId,
        weekStartDate: snapshot.weekStartDate,
        weekEndDate: snapshot.weekEndDate,
        status: snapshot.status,
        attestationAccepted: snapshot.attestationAccepted,
        signedByName: snapshot.signedByName,
        signatureValue: snapshot.signatureValue,
        signedDate: snapshot.signedDate,
        regularRows: snapshot.regularRows,
        callRows: snapshot.callRows,
        callbackRows: snapshot.callbackRows,
        hospitalOptions: snapshot.hospitalOptions
      };
    },
    [
      attestationAccepted,
      callRows,
      callbackRows,
      contractorId,
      currentStatus,
      hospitalOptions,
      regularRows,
      selectedTimecardId,
      signatureValue,
      signedByName,
      signedDate,
      weekEndDate,
      weekStartDate
    ]
  );

  const getCurrentTimecardSnapshotString = React.useCallback(
    (payload) => JSON.stringify(getCurrentTimecardSnapshot(payload)),
    [getCurrentTimecardSnapshot]
  );

  const buildSnapshotOverrides = React.useCallback(
    (snapshotOverrides = null, submit = false) => ({
      timecardId: snapshotOverrides?.timecardId ?? selectedTimecardId,
      contractorId,
      weekStartDate: snapshotOverrides?.weekStartDate ?? weekStartDate,
      weekEndDate: snapshotOverrides?.weekEndDate ?? weekEndDate,
      status: submit ? "submitted" : "draft",
      attestationAccepted: snapshotOverrides?.attestationAccepted ?? attestationAccepted,
      signedByName: snapshotOverrides?.signedByName ?? signedByName,
      signatureValue: snapshotOverrides?.signatureValue ?? signatureValue,
      signedDate: snapshotOverrides?.signedDate ?? signedDate,
      regularRows: snapshotOverrides?.regularRows ?? regularRows,
      callRows: snapshotOverrides?.callRows ?? callRows,
      callbackRows: snapshotOverrides?.callbackRows ?? callbackRows,
      hospitalOptions: snapshotOverrides?.hospitalOptions ?? hospitalOptions
    }),
    [
      attestationAccepted,
      callRows,
      callbackRows,
      contractorId,
      hospitalOptions,
      regularRows,
      selectedTimecardId,
      signatureValue,
      signedByName,
      signedDate,
      weekEndDate,
      weekStartDate
    ]
  );

  const isTimecardDirty = React.useCallback(
    (snapshotOverrides = null) =>
      getCurrentTimecardSnapshotString(buildSnapshotOverrides(snapshotOverrides)) !==
      lastSavedPayloadRef.current,
    [buildSnapshotOverrides, getCurrentTimecardSnapshotString]
  );

  const markCurrentPayloadSaved = React.useCallback(
    (payload) => {
      lastSavedPayloadRef.current = getCurrentTimecardSnapshotString(payload);
    },
    [getCurrentTimecardSnapshotString]
  );

  const loadTimecardById = React.useCallback(
    async (nextTimecardId) => {
      invalidateFormContext();
      setSelectedTimecardId(nextTimecardId);
      setSaveError("");

      if (!nextTimecardId) {
        resetTimecardForm();
        return;
      }

      const timecard = await fetchTimecard(nextTimecardId);
      if (!timecard) {
        throw new Error("Selected timecard could not be loaded.");
      }

      const formState = mapTimecardToFormState(timecard, hospitalOptions);
      setRegularRows(formState.regularRows);
      setCallRows(formState.callRows);
      setCallbackRows(formState.callbackRows);
      setWeekStartDate(formState.weekStartDate || getDefaultWeekRange()[0]);
      setWeekEndDate(formState.weekEndDate || getDefaultWeekRange()[1]);
      setAttestationAccepted(formState.attestationAccepted);
      setSignedByName(formState.signedByName);
      setSignatureValue(formState.signatureValue);
      setSignedDate(formState.signedDate);
      setCurrentStatus(formState.status ?? "draft");

      lastSavedPayloadRef.current = JSON.stringify({
        timecardId: nextTimecardId,
        contractorId,
        weekStartDate: formState.weekStartDate || getDefaultWeekRange()[0],
        weekEndDate: formState.weekEndDate || getDefaultWeekRange()[1],
        status: formState.status ?? "draft",
        attestationAccepted: formState.attestationAccepted,
        signedByName: formState.signedByName,
        signatureValue: formState.signatureValue,
        signedDate: formState.signedDate,
        regularRows: formState.regularRows,
        callRows: formState.callRows,
        callbackRows: formState.callbackRows,
        hospitalOptions
      });
    },
    [contractorId, hospitalOptions, invalidateFormContext, resetTimecardForm]
  );

  const reloadTimecardList = React.useCallback(async () => {
    const list = await fetchMyTimecards();
    setTimecards(list);
    return list;
  }, []);

  const loadTimecardForCurrentWeek = React.useCallback(async () => {
    setTimecardLoadError("");
    setTimecardsLoading(true);
    setSaveError("");

    try {
      const list = await reloadTimecardList();
      const weekStartDateStr = formatLocalDate(currentWeekStart);
      const existingTimecard = list.find(
        (timecard) => (timecard.weekStartDate ?? timecard.WeekStartDate) === weekStartDateStr
      );

      if (existingTimecard) {
        const timecardId = existingTimecard.timecardId ?? existingTimecard.TimecardId;
        await loadTimecardById(timecardId);
      } else {
        resetTimecardForm();
        setWeekStartDate(weekStartDateStr);
        setWeekEndDate(formatLocalDate(currentWeekEnd));
      }
    } catch (error) {
      console.error(error);
      setTimecardLoadError(error?.message ?? "Unable to load your timecards.");
    } finally {
      setTimecardsLoading(false);
    }
  }, [currentWeekEnd, currentWeekStart, loadTimecardById, reloadTimecardList, resetTimecardForm]);

  React.useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    fetchHospitalOptions(abortController.signal)
      .then((hospitals) => {
        if (!isMounted) {
          return;
        }

        setHospitalOptions(hospitals);
      })
      .catch((error) => {
        if (error.name !== "AbortError" && isMounted) {
          console.error("Unable to load hospitals from the database.", error);
          setHospitalLoadError("Unable to load hospitals from the database.");
        }
      })
      .finally(() => {
        if (isMounted) {
          setHospitalLoading(false);
        }
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  React.useEffect(() => {
    if (hospitalOptions.length > 0) {
      loadTimecardForCurrentWeek();
    }
  }, [currentWeekOffset, hospitalOptions, loadTimecardForCurrentWeek]);

  React.useEffect(() => {
    const dialog = submitWeekDialogRef.current;
    if (!dialog) {
      return;
    }

    if (showSubmitWeekDialog) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else if (dialog.open) {
      dialog.close();
    }
  }, [showSubmitWeekDialog]);

  React.useEffect(() => {
    if (lastSavedPayloadRef.current === null) {
      lastSavedPayloadRef.current = getCurrentTimecardSnapshotString();
    }
  }, [getCurrentTimecardSnapshotString]);

  const performSave = React.useCallback(
    async ({ submit = false, silent = false, snapshotOverrides = null, throwOnError = false } = {}) => {
      if (isSaving) {
        // A caller that needs a guaranteed flush (throwOnError) must not silently
        // proceed while another save is mid-flight — surface it so it can abort/retry.
        if (throwOnError) {
          throw new Error("A save is already in progress. Please try again in a moment.");
        }
        return;
      }

      const snapshot = buildSnapshotOverrides(snapshotOverrides, submit);
      const wasPreviouslySubmitted = currentStatus === "submitted" || currentStatus === "approved";

      if (!snapshot.weekStartDate || !snapshot.weekEndDate) {
        if (!silent) {
          setSaveError("Unable to determine the current week for this timecard.");
        }
        return;
      }

      if (submit && !snapshot.attestationAccepted) {
        if (!silent) {
          setSaveError("Attestation must be accepted before submitting.");
        }
        return;
      }

      // Compare completion times with the last persisted payload so only a
      // completion time that remains in this save can trigger the prompt.
      const includesTimeOutChange = hasNewCompletionTime(snapshot, lastSavedPayloadRef.current);
      const saveContextVersion = formContextVersionRef.current;
      let showPromptAfterSave = false;

      setIsSaving(true);
      if (!silent) {
        setSaveError("");
      }

      try {
        const payload = buildTimecardPayload(snapshot);

        const savedTimecard = await saveTimecard(payload);
        const savedId = savedTimecard?.timecardId ?? savedTimecard?.TimecardId ?? 0;
        const savedStatus = savedTimecard?.status ?? savedTimecard?.Status ?? payload.status;
        const savedSnapshot = {
          ...snapshot,
          timecardId: savedId || snapshot.timecardId,
          status: savedStatus
        };

        if (saveContextVersion !== formContextVersionRef.current) {
          return;
        }

        setSelectedTimecardId(savedSnapshot.timecardId);
        setCurrentStatus(savedStatus);
        markCurrentPayloadSaved(savedSnapshot);

        if (
          includesTimeOutChange &&
          wasPreviouslySubmitted
        ) {
          // The completion time was persisted. Prompt after the full save flow
          // finishes so the dialog cannot race the isSaving guard, regardless of
          // whether another shift is currently active.
          showPromptAfterSave = true;
        }

        if (submit && savedSnapshot.timecardId > 0) {
          try {
            const submitResult = await submitTimecard(savedSnapshot.timecardId);
            const submittedStatus = submitResult?.status ?? submitResult?.Status ?? "submitted";
            setCurrentStatus(submittedStatus);
          } catch (submitError) {
            console.error("Timecard submission email failed:", submitError);
          }
        }

        if (!silent) {
          showToast(submit ? "Timecard submitted successfully." : "Draft saved successfully.");
        }

        await reloadTimecardList();
      } catch (error) {
        console.error(error);
        if (!silent) {
          setSaveError(error?.message ?? "Unable to save the timecard.");
        }
        if (throwOnError) throw error;
      } finally {
        setIsSaving(false);
        if (showPromptAfterSave && saveContextVersion === formContextVersionRef.current) {
          setShowResubmitAfterEditDialog(true);
        }
      }
    },
    [
      buildSnapshotOverrides,
      currentStatus,
      currentWeekOffset,
      isSaving,
      markCurrentPayloadSaved,
      reloadTimecardList
    ]
  );

  React.useEffect(() => {
    if (isSaving || !pendingSilentSaveRef.current) {
      return;
    }

    const snapshotOverrides = pendingSilentSaveRef.current;
    pendingSilentSaveRef.current = null;
    void performSave({ submit: false, silent: true, snapshotOverrides });
  }, [isSaving, performSave]);

  const handleClockAction = React.useCallback(
    async (snapshotOverrides = null) => {
      if (isSaving || !isTimecardDirty(snapshotOverrides)) {
        return;
      }

      await performSave({ submit: false, silent: true, snapshotOverrides });
    },
    [isSaving, isTimecardDirty, performSave]
  );

  const handleClockInAction = React.useCallback(
    async (snapshotOverrides = null) => {
      if (isSaving) return;
      await performSave({ submit: false, silent: true, snapshotOverrides, throwOnError: true });
    },
    [isSaving, performSave]
  );

  const hasActiveShift = React.useMemo(() => {
    return snapshotHasActiveShift(
      { regularRows, callRows, callbackRows },
      currentWeekOffset
    );
  }, [regularRows, callRows, callbackRows, currentWeekOffset]);

  const handleResubmit = async (reason) => {
    if (!selectedTimecardId) {
      setSaveError("Select a saved timecard before resubmitting.");
      throw new Error("No timecard selected.");
    }

    try {
      // Flush any pending edits first so admin's re-emailed copy reflects the latest
      // state. throwOnError aborts the resubmit if the save fails, so we never notify
      // admin with a stale copy. Autosave/PUT no longer emails, so this is the only
      // path that notifies admin of a change.
      await performSave({ submit: false, silent: true, throwOnError: true });
      const result = await resubmitTimecard(selectedTimecardId, reason);
      const emailed = result?.emailed ?? true;
      setCurrentStatus(result?.status ?? result?.Status ?? "submitted");
      showToast(
        emailed
          ? "Timecard resubmitted."
          : "Timecard resubmitted, but the notification email could not be sent."
      );
      await reloadTimecardList();
    } catch (error) {
      console.error(error);
      setSaveError(error?.message ?? "Unable to resubmit the timecard.");
      // Re-throw so the resubmit prompt stays open for a retry.
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedTimecardId) {
      setSaveError("Select a saved timecard before deleting.");
      return;
    }

    invalidateFormContext();

    try {
      await deleteTimecard(selectedTimecardId);
      showToast("Timecard deleted.");
      await reloadTimecardList();
      resetTimecardForm();
    } catch (error) {
      console.error(error);
      setSaveError(error?.message ?? "Unable to delete the timecard.");
    }
  };

  const handleTimeInputBlur = (event) => {
    if (event.target?.type === "time") {
      void handleClockAction();
    }
  };

  const navigateWeek = React.useCallback((direction) => {
    invalidateFormContext();
    setDraftSubmitReviewId(0);
    setCurrentWeekOffset((previousOffset) => previousOffset + direction);
  }, [invalidateFormContext]);

  const goToTimecardWeek = React.useCallback(
    async (draftChoice, markReadyToSubmit = false) => {
      if (!draftChoice?.timecardId) {
        return;
      }

      const weekOffset = getWeekOffsetForStartDate(draftChoice.weekStartDate, baseWeekStartDate);
      setCurrentWeekOffset(weekOffset);
      await loadTimecardById(draftChoice.timecardId);
      setDraftSubmitReviewId(markReadyToSubmit ? draftChoice.timecardId : 0);
    },
    [baseWeekStartDate, loadTimecardById]
  );

  const flushCurrentDraftSave = React.useCallback(async () => {
    const pendingSnapshotOverrides = pendingSilentSaveRef.current;
    pendingSilentSaveRef.current = null;

    if (pendingSnapshotOverrides || isTimecardDirty()) {
      await performSave({
        submit: false,
        silent: true,
        snapshotOverrides: pendingSnapshotOverrides,
        throwOnError: true
      });
    }
  }, [isTimecardDirty, performSave]);

  const handleDraftTimecardSelect = React.useCallback(
    async (draftChoice) => {
      try {
        setSaveError("");
        await flushCurrentDraftSave();
        await goToTimecardWeek(draftChoice, false);
      } catch (error) {
        console.error(error);
        setSaveError(error?.message ?? "Unable to load the selected draft timecard.");
      }
    },
    [flushCurrentDraftSave, goToTimecardWeek]
  );

  const openSubmitWeekDialog = React.useCallback(async () => {
    try {
      setSaveError("");
      setSelectedSubmitDraftId(0);

      await flushCurrentDraftSave();

      const refreshedTimecards = await reloadTimecardList();
      const refreshedDrafts = buildDraftTimecardChoices(refreshedTimecards, baseWeekStartDate);

      if (!refreshedDrafts.length) {
        setSaveError("No draft timecards are available to submit.");
        return;
      }

      setTimecards(refreshedTimecards);
      setShowSubmitWeekDialog(true);
    } catch (error) {
      console.error(error);
      setSaveError(error?.message ?? "Unable to prepare your draft timecards for submission.");
    }
  }, [baseWeekStartDate, flushCurrentDraftSave, reloadTimecardList]);

  const handleSubmitClick = React.useCallback(() => {
    if (readyToSubmitSelectedDraft) {
      void performSave({ submit: true, silent: false });
      return;
    }

    void openSubmitWeekDialog();
  }, [openSubmitWeekDialog, performSave, readyToSubmitSelectedDraft]);

  const handleSubmitWeekDialogDismiss = React.useCallback(() => {
    setShowSubmitWeekDialog(false);
    setSelectedSubmitDraftId(0);
  }, []);

  const handleReviewSelectedDraft = React.useCallback(async () => {
    const draftChoice = draftTimecards.find((draft) => draft.timecardId === Number(selectedSubmitDraftId));
    if (!draftChoice) {
      setSaveError("Choose a draft week to review.");
      return;
    }

    setShowSubmitWeekDialog(false);
    setSelectedSubmitDraftId(0);

    try {
      await goToTimecardWeek(draftChoice, true);
      setSaveError("");
    } catch (error) {
      console.error(error);
      setSaveError(error?.message ?? "Unable to load the selected draft timecard.");
    }
  }, [draftTimecards, goToTimecardWeek, selectedSubmitDraftId]);

  const goToCurrentWeek = React.useCallback(() => {
    invalidateFormContext();
    setDraftSubmitReviewId(0);
    setCurrentWeekOffset(0);
  }, [invalidateFormContext]);

  return (
    <div className="timecard-page">
      <AppHeader
        title="Weekly timecard"
        session={session}
        onLogout={onLogout}
        logoUrl={logoUrl}
      />

      <main className="timecard-workspace" onBlurCapture={handleTimeInputBlur}>
        {(hospitalLoading || hospitalLoadError) && (
          <div className="hospital-load-status">
            {hospitalLoading ? "Loading hospitals..." : hospitalLoadError}
          </div>
        )}

        {currentWeekOffset === 0 && !timecardsLoading && (
          <TodaySection
            regularRows={regularRows}
            callRows={callRows}
            callbackRows={callbackRows}
            onRegularRowChange={handleRegularRowChange}
            onRegularTookBreakChange={handleRegularTookBreakChange}
            onCallRowChange={handleCallRowChange}
            onCallBackRowChange={handleCallBackRowChange}
            hospitalOptions={hospitalOptions}
            onClockAction={handleClockAction}
            onClockInAction={handleClockInAction}
            onClockOutAction={handleClockInAction}
            onDeleteTodayShift={handleClearTodayRow}
            onAddRegularRow={handleAddRegularRow}
            getCallHospitalForDay={(day) => callRows.find(r => r.day === day)?.hospital ?? ""}
          />
        )}

        <div className="week-section">
          <h2 className="weekly-section-heading section-title">This Week</h2>

          <section className="sheet-section contractor-control-panel" aria-labelledby="timecard-control-heading">
          <div className="week-navigator">
            <button
              type="button"
              className="week-nav-button"
              onClick={() => navigateWeek(-1)}
              aria-label="Previous week"
            >
              ‹
            </button>
            <h3 className="week-range">{currentWeekRange}</h3>
            {selectedTimecardId > 0 && (
              <span className={`timecard-status-badge timecard-status-badge-${currentStatus}`}>
                {TIMECARD_STATUS_LABELS[currentStatus] ?? currentStatus}
              </span>
            )}
            <button
              type="button"
              className="week-nav-button"
              onClick={() => navigateWeek(1)}
              aria-label="Next week"
            >
              ›
            </button>
          </div>

          {currentWeekOffset !== 0 && (
            <div className="week-meta-row">
              <button
                type="button"
                className="week-current-button"
                onClick={goToCurrentWeek}
              >
                Return to current week
              </button>
            </div>
          )}

          <div className="draft-timecard-list" aria-label="Draft timecards">
            <div className="draft-timecard-list-heading">
              <span>Draft timecards</span>
              {timecardsLoading ? <span className="inline-status">Loading...</span> : null}
            </div>
            {draftTimecards.length ? (
              <div className="draft-timecard-items">
                {draftTimecards.map((draft) => (
                  <button
                    key={draft.timecardId}
                    type="button"
                    className={`draft-timecard-item${draft.timecardId === selectedTimecardId ? " draft-timecard-item-active" : ""}`}
                    onClick={() => void handleDraftTimecardSelect(draft)}
                  >
                    <span>{draft.weekRange || draft.weekStartDate}</span>
                    {draft.isCurrentWeek ? <span className="draft-timecard-marker">Current week</span> : null}
                  </button>
                ))}
              </div>
            ) : (
              <p className="draft-timecard-empty">No draft timecards.</p>
            )}
          </div>

          {timecardLoadError ? (
            <div className="status-message error" role="alert">
              {timecardLoadError}
            </div>
          ) : null}
          </section>
        </div>

        <TimecardEntryArea
          activeTab={activeTab}
          onTabChange={setActiveTab}
          regularRows={regularRows}
          callRows={callRows}
          callbackRows={callbackRows}
          onRegularRowChange={handleRegularRowChange}
          onRegularTookBreakChange={handleRegularTookBreakChange}
          onCallRowChange={handleCallRowChange}
          onCallBackRowChange={handleCallBackRowChange}
          hospitalOptions={hospitalOptions}
          weekStartDate={weekStartDate}
          onClockAction={handleClockAction}
        />

        <ApprovalSection
          attestationAccepted={attestationAccepted}
          signedByName={signedByName}
          signedDate={signedDate}
          onAttestationChange={handleAttestationChange}
          onSignedByNameChange={handleSignedByNameChange}
          onSignedDateChange={handleSignedDateChange}
          onSubmit={handleSubmitClick}
          onResubmit={handleResubmit}
          onDelete={handleDelete}
          canDelete={!!selectedTimecardId}
          statusMessage={saveError}
          status={currentStatus}
          hasActiveShift={hasActiveShift}
          submitLabel={submitButtonLabel}
        />
      </main>

      <Toast message={toastMessage} visible={toastVisible} onDismiss={dismissToast} />
      {showSubmitWeekDialog ? (
        <dialog
          ref={submitWeekDialogRef}
          className="submit-week-dialog"
          aria-labelledby="submit-week-dialog-title"
          onCancel={(event) => {
            event.preventDefault();
            handleSubmitWeekDialogDismiss();
          }}
          onClick={(event) => {
            if (event.target === submitWeekDialogRef.current) {
              handleSubmitWeekDialogDismiss();
            }
          }}
        >
          <div className="submit-week-dialog-inner">
            <h2 id="submit-week-dialog-title">Choose week to submit</h2>
            <p>
              Select the draft week you want to review and submit.
            </p>
            <div className="submit-week-options" role="radiogroup" aria-label="Draft weeks">
              {draftTimecards.map((draft) => (
                <label key={draft.timecardId} className="submit-week-option">
                  <input
                    type="radio"
                    name="submit-week"
                    value={draft.timecardId}
                    checked={Number(selectedSubmitDraftId) === draft.timecardId}
                    onChange={() => setSelectedSubmitDraftId(draft.timecardId)}
                  />
                  <span className="submit-week-option-copy">
                    <span className="submit-week-option-range">{draft.weekRange || draft.weekStartDate}</span>
                    <span className="submit-week-option-meta">
                      Draft timecard
                      {draft.isCurrentWeek ? <span className="draft-timecard-marker">Current week</span> : null}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="submit-week-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={handleSubmitWeekDialogDismiss}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-action"
                onClick={handleReviewSelectedDraft}
                disabled={!selectedSubmitDraftId}
              >
                Review selected week
              </button>
            </div>
          </div>
        </dialog>
      ) : null}
      <ResubmitAfterEditDialog
        open={showResubmitAfterEditDialog}
        onResubmit={handleResubmit}
        onDismiss={() => setShowResubmitAfterEditDialog(false)}
      />
    </div>
  );
}
